import * as React from 'react'

import { apiJson } from '@/lib/api'
import type { Asset, AssetFolder, ReferenceVideo, UnifiedAsset } from '@/types'

interface FolderListPayload {
  items: AssetFolder[]
  total: number
}

interface AssetListPayload {
  items: Asset[]
  total: number
}

interface ReferenceListPayload {
  items: ReferenceVideo[]
  total: number
}

function normalizeAsset(asset: Asset, folder: AssetFolder | null): UnifiedAsset {
  return {
    id: `asset-${asset.id}`,
    sourceType: 'asset',
    sourceId: asset.id,
    name: asset.original_filename || asset.filename,
    fileType: asset.file_type,
    analysisStatus: asset.analysis_status,
    folderName: folder?.name ?? null,
    folderId: asset.folder_id,
    folderType: folder?.folder_type ?? null,
    tags: asset.ai_tags,
    aiDescription: asset.ai_description,
    fileSize: asset.file_size_bytes,
    createdAt: asset.created_at,
    isStarred: asset.is_starred ?? false,
    originalAsset: asset,
  }
}

const PAGE_SIZE = 200
const MAX_PAGES_PER_FOLDER = 25 // hard cap → 5000 assets/folder

async function fetchAllAssetsForFolder(folderId: number): Promise<Asset[]> {
  const all: Asset[] = []
  let offset = 0
  for (let page = 0; page < MAX_PAGES_PER_FOLDER; page += 1) {
    const data = await apiJson<AssetListPayload>(
      `/api/v1/folders/${folderId}/assets?limit=${PAGE_SIZE}&offset=${offset}`,
    )
    const items = data.items || []
    all.push(...items)
    offset += items.length
    if (items.length < PAGE_SIZE || offset >= (data.total ?? 0)) break
  }
  return all
}

function normalizeReference(ref: ReferenceVideo): UnifiedAsset {
  return {
    id: `ref-${ref.id}`,
    sourceType: 'reference',
    sourceId: ref.id,
    name: ref.title,
    fileType: 'video',
    analysisStatus: ref.analysis_status,
    folderName: null,
    folderId: null,
    folderType: null,
    tags: ref.style_profile?.prompt_hints ?? null,
    aiDescription: typeof ref.style_profile?.mood === 'string'
      ? ref.style_profile.mood
      : ref.style_profile?.mood?.overall_mood ?? null,
    fileSize: ref.file_size_bytes,
    createdAt: ref.created_at,
    originalReference: ref,
  }
}

export function useAllAssets(pollMs = 15000) {
  const [items, setItems] = React.useState<UnifiedAsset[]>([])
  const [folders, setFolders] = React.useState<AssetFolder[]>([])
  const [loading, setLoading] = React.useState(true)
  const [error, setError] = React.useState<string | null>(null)

  const load = React.useCallback(async () => {
    try {
      // Fetch folders
      const folderData = await apiJson<FolderListPayload>('/api/v1/folders')
      const folderList = folderData.items || []
      setFolders(folderList)

      // Fetch assets from all folders in parallel (paginated under the hood)
      const assetPromises = folderList.map((folder) =>
        fetchAllAssetsForFolder(folder.id)
          .then((assets) => ({ folder, assets }))
          .catch(() => ({ folder, assets: [] as Asset[] })),
      )

      // Fetch references
      const refPromise = apiJson<ReferenceListPayload>('/api/v1/references?limit=200')
        .then((data) => data.items || [])
        .catch(() => [] as ReferenceVideo[])

      const [folderAssets, references] = await Promise.all([
        Promise.all(assetPromises),
        refPromise,
      ])

      // Normalize everything
      const unified: UnifiedAsset[] = []

      for (const { folder, assets } of folderAssets) {
        for (const asset of assets) {
          unified.push(normalizeAsset(asset, folder))
        }
      }

      for (const ref of references) {
        unified.push(normalizeReference(ref))
      }

      // Sort newest first
      unified.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())

      setItems(unified)
      setError(null)
    } catch (err) {
      setError((err as Error).message)
    } finally {
      setLoading(false)
    }
  }, [])

  React.useEffect(() => {
    load()
    const hasActive = items.some(
      (i) => i.analysisStatus === 'pending' || i.analysisStatus === 'processing',
    )
    const interval = window.setInterval(load, hasActive ? pollMs : pollMs * 3)
    return () => window.clearInterval(interval)
  }, [load, pollMs, items.length])

  return { items, folders, loading, error, refresh: load }
}
